import { useQuery } from "@apollo/client";
import Construction from "@mui/icons-material/Construction";
import FilterList from "@mui/icons-material/FilterList";
import { Box, Chip, Stack, Tooltip, Typography, useMediaQuery, useTheme } from "@mui/material";
import { lighten } from "@mui/material/styles";
import { GridSortModel } from "@mui/x-data-grid-premium";
import { CommandButton } from "components/CommandButton";
import { DataGrid } from "components/DataGrid";
import { Omnisearch } from "components/Omnisearch/Omnisearch";
// Deep import from `src/utils/...` bypasses the package's top-level
// barrel, which transitively pulls in `encamp-prisma/generated/client`
// (server-only). The Vite `prisma-client-guard` plugin would otherwise
// fail the browser bundle. Other oak callers use this same pattern —
// see `Omnisearch.tsx` / `FilterPopover.tsx`.
import { extractKeyedSearchTerms } from "encamp-omnisearch/src/utils/searchTerms";
import {
  FeatureFlagKey,
  getFeatureFlagMeta,
  isFeatureFlagKey,
} from "encamp-shared/src/featureFlags";
import {
  FilterKey,
  OmnisearchGridColDef,
  useOmnisearchDatagrid,
} from "hooks/useOmnisearchDatagrid";
import { useCallback, useMemo, useState } from "react";

import EncampLogo from "../../../assets/EncampLogo.svg";
import { gql } from "../../../generated-graphql";
import { StaffFlagsListQuery } from "../../../generated-graphql/graphql";
import { formatPercent, stripModuleSuffix } from "./flagAdoption";
import { FlagDetailDrawer } from "./FlagDetailDrawer";
import { useCustomerTenantCount } from "./useCustomerTenantCount";

type GridSortItem = GridSortModel[number];

type FeatureFlagFragment = StaffFlagsListQuery["featureFlags"][number];

/**
 * Pulls the full set of feature flags in one shot. There are fewer than
 * ~50 flags total, so we don't paginate, filter, or sort on the server —
 * everything happens client-side via `applyFlagFilters` and
 * `sortFlagRows`. Intentionally takes no variables so the GraphQL
 * `NoUnusedVariablesRule` is satisfied (we deliberately avoid using
 * `OmnisearchDataDisplay`, which always injects `$search/$page/...`).
 */
const STAFF_FLAGS_LIST_QUERY = gql(`
  query StaffFlagsList {
    featureFlags {
      key
      label
      description
      type
      parentKey
      defaultValue
      staffOverride
      inDevelopment
      requires
      notes
      tags
      annotationUpdatedAt
      annotationUpdatedBy
      tenantOnCount
      tenantOffCount
    }
  }
`);

/**
 * The on-screen flag row. Mirrors the Apollo result type but with a
 * narrowed `key` (the key column is the row id, and we want callers like
 * `FlagDetailDrawer` to be able to assume it's a real `FeatureFlagKey`).
 */
export type FlagRow = {
  key: FeatureFlagKey;
  label: string;
  description: string;
  type: "MODULE" | "ADD_ON" | "OTHER";
  parentKey: string | null;
  parentLabel: string | null;
  defaultValue: boolean;
  staffOverride: boolean;
  inDevelopment: boolean;
  requires: string[];
  notes: string | null;
  tags: string[];
  annotationUpdatedAt: string | null;
  annotationUpdatedBy: string | null;
  tenantOnCount: number;
  tenantOffCount: number;
  hasAnnotation: boolean;
};

const TYPE_LABEL: Record<FlagRow["type"], string> = {
  MODULE: "Module",
  ADD_ON: "Add-on",
  OTHER: "Other",
};

const TYPE_FILTER_VALUES = Object.keys(TYPE_LABEL) as FlagRow["type"][];

/**
 * Filter chips wired into the omnisearch popover. All are evaluated
 * client-side by `applyFlagFilters` below; nothing is forwarded to the
 * server because the server-side resolver doesn't accept these args.
 */
const FLAG_FILTER_COLUMNS: FilterKey[] = [
  {
    key: "type",
    header: "Type",
    filterKeyType: "enumAutocomplete",
    enumValues: TYPE_FILTER_VALUES,
    enumPresentationFunction: (v) => TYPE_LABEL[v as FlagRow["type"]] ?? v,
  },
  { key: "staffOnly", header: "Staff-visible", filterKeyType: "boolean" },
  { key: "inDevelopment", header: "In development", filterKeyType: "boolean" },
  { key: "defaultOn", header: "Default on", filterKeyType: "boolean" },
  { key: "hasAnnotation", header: "Has annotation", filterKeyType: "boolean" },
];

/**
 * Adoption stat cell — count + parenthesized percent in muted text.
 * Rendered as a two-column grid so counts line up on one right edge and
 * percentages line up on another, regardless of their digit lengths
 * (e.g. `5  (10%)` and `120  (75%)` produce visually aligned columns).
 * The percent is in `text.secondary` to deemphasize it relative to the
 * raw count; the tooltip spells out absolute numbers.
 */
function AdoptionCell({
  numerator,
  denominator,
  offCount,
}: {
  numerator: number;
  denominator: number;
  offCount: number;
}) {
  // One line per stat so the tooltip reads as a quick legend rather
  // than a run-on sentence. We always show enabled + disabled (even
  // when zero) so the order is predictable; the "total" row only
  // appears when we actually have a denominator to anchor it to.
  const tooltipContent = (
    <Stack spacing={0} sx={{ fontVariantNumeric: "tabular-nums" }}>
      <Box>{numerator} explicitly enabled</Box>
      <Box>{offCount} explicitly disabled</Box>
      {denominator > 0 && <Box>{denominator} active customer tenants</Box>}
    </Stack>
  );
  return (
    <Tooltip title={tooltipContent} placement="top">
      <Box
        sx={{
          width: "100%",
          display: "grid",
          // `auto auto` keeps each sub-column tight to its content while
          // still right-aligning each value within its slot — counts
          // line up on one right edge, percentages on another.
          gridTemplateColumns: "auto auto",
          columnGap: 0.5,
          justifyContent: "end",
          fontVariantNumeric: "tabular-nums",
        }}
      >
        <Box sx={{ textAlign: "right" }}>{numerator}</Box>
        <Box sx={{ textAlign: "right", color: "text.secondary", minWidth: 44 }}>
          {denominator > 0 ? `(${formatPercent(numerator, denominator)})` : ""}
        </Box>
      </Box>
    </Tooltip>
  );
}

function FlagLabelCell({ row }: { row: FlagRow }) {
  // The label is a short identifier; the FF's description carries the
  // explanatory copy. Surface it on hover so staff can scan the grid
  // without opening the detail drawer for every row.
  const label = (
    <Typography variant="body2" sx={{ py: 0.5 }} noWrap>
      {row.label}
    </Typography>
  );
  if (!row.description) return label;
  return (
    <Tooltip title={row.description} placement="top">
      {label}
    </Tooltip>
  );
}

function ParentModuleCell({ row }: { row: FlagRow }) {
  if (!row.parentLabel) return null;
  return (
    <Typography variant="body2" noWrap>
      {row.parentLabel}
    </Typography>
  );
}

function StaffOnlyCell({ row }: { row: FlagRow }) {
  if (!row.staffOverride) return null;
  return (
    <Tooltip title="Staff users see this feature regardless of the flag value">
      <Box
        component="img"
        src={EncampLogo}
        alt="Staff-visible"
        aria-label="Staff-visible"
        sx={{ width: 18, height: 18, display: "block" }}
      />
    </Tooltip>
  );
}

function InDevelopmentCell({ row }: { row: FlagRow }) {
  if (!row.inDevelopment) return null;
  return (
    <Tooltip title="This feature is currently being developed">
      <Construction
        fontSize="small"
        // Muted (text.secondary) so the icon reads as neutral metadata
        // rather than a warning — "in development" is informational,
        // not a problem.
        sx={{ color: "text.secondary", display: "block" }}
        aria-label="In Development"
      />
    </Tooltip>
  );
}

function TagsCell({ row }: { row: FlagRow }) {
  if (row.tags.length === 0) return null;
  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ py: 0.5 }}>
      {row.tags.map((tag) => (
        <Chip key={tag} label={tag} size="small" />
      ))}
    </Stack>
  );
}

function matchesBoolean(filterValue: string | undefined, rowValue: boolean): boolean {
  if (filterValue === undefined) return true;
  return filterValue === "true" ? rowValue : !rowValue;
}

/**
 * Apply omnisearch chips + free-text search to the in-memory flag set.
 * Filtering happens entirely client-side — there are fewer than ~50
 * flags so even a naive sweep is cheap, and the GraphQL resolver doesn't
 * accept these args anyway.
 *
 * Parses the raw omnisearch string here (rather than receiving an
 * already-parsed result) because we also want the free-text portion —
 * `extractKeyedSearchTerms` returns `[keyValuePairs, freeText]` in one
 * pass, and re-using it keeps the chip semantics aligned with the rest
 * of the app's omnisearch behavior.
 */
export function applyFlagFilters(flags: FlagRow[], omnisearch: string): FlagRow[] {
  const [keyValuePairs, freeTextRaw] = extractKeyedSearchTerms(omnisearch);
  // Group keyed values by key; multi-value chips (e.g. type:MODULE|ADD_ON)
  // become arrays here.
  const byKey = new Map<string, string[]>();
  for (const { key, value } of keyValuePairs) {
    const existing = byKey.get(key) ?? [];
    byKey.set(key, [...existing, ...value]);
  }
  const types = byKey.get("type") ?? [];
  const staffOnly = byKey.get("staffOnly")?.[0];
  const inDev = byKey.get("inDevelopment")?.[0];
  const defaultOn = byKey.get("defaultOn")?.[0];
  const hasAnnotation = byKey.get("hasAnnotation")?.[0];
  const tagFilter = byKey.get("tag") ?? [];
  const freeText = freeTextRaw.trim().toLowerCase();

  return flags.filter((flag) => {
    if (types.length > 0 && !types.includes(flag.type)) return false;
    if (!matchesBoolean(staffOnly, flag.staffOverride)) return false;
    if (!matchesBoolean(inDev, flag.inDevelopment)) return false;
    if (!matchesBoolean(defaultOn, flag.defaultValue)) return false;
    if (!matchesBoolean(hasAnnotation, flag.hasAnnotation)) return false;
    if (tagFilter.length > 0 && !tagFilter.some((t) => flag.tags.includes(t))) return false;
    if (freeText.length > 0) {
      const haystack = `${flag.label} ${flag.key} ${flag.description}`.toLowerCase();
      if (!haystack.includes(freeText)) return false;
    }
    return true;
  });
}

/**
 * Sort the in-memory rows. We deliberately avoid the DataGrid's
 * built-in client-side sort to keep the row order stable across
 * filter / pagination changes (and so the tests can lock the order).
 */
export function sortFlagRows(rows: FlagRow[], sortItem: GridSortItem | undefined): FlagRow[] {
  if (!sortItem) return rows;
  const { field, sort } = sortItem;
  if (!sort) return rows;
  const dir = sort === "asc" ? 1 : -1;
  const cmpString = (a: string | null | undefined, b: string | null | undefined): number => {
    const av = (a ?? "").toLowerCase();
    const bv = (b ?? "").toLowerCase();
    if (av < bv) return -1 * dir;
    if (av > bv) return 1 * dir;
    return 0;
  };
  const cmpNumber = (a: number, b: number): number => (a - b) * dir;
  const cmpBool = (a: boolean, b: boolean): number => cmpNumber(a ? 1 : 0, b ? 1 : 0);
  const sorted = [...rows];
  sorted.sort((a, b) => {
    switch (field) {
      case "label":
        return cmpString(a.label, b.label);
      case "type":
        return cmpString(a.type, b.type);
      case "parentLabel":
        return cmpString(a.parentLabel, b.parentLabel);
      case "tenantOnCount":
        return cmpNumber(a.tenantOnCount, b.tenantOnCount);
      case "annotationUpdatedAt":
        return cmpString(a.annotationUpdatedAt, b.annotationUpdatedAt);
      case "staffOverride":
        return cmpBool(a.staffOverride, b.staffOverride);
      case "inDevelopment":
        return cmpBool(a.inDevelopment, b.inDevelopment);
      case "defaultValue":
        return cmpBool(a.defaultValue, b.defaultValue);
      default:
        return 0;
    }
  });
  return sorted;
}

/**
 * Convert the GraphQL response into the on-screen `FlagRow` shape used
 * by the grid + drawer. We resolve `parentLabel` here so the parent-
 * module column can render and sort without a second lookup per row.
 *
 * For grouping purposes a module is its own "parent" — every flag that
 * belongs to a module (including the module itself) shares the same
 * value in the Parent module column. We also strip the trailing
 * " module" word from the displayed label since the column header
 * already says "Parent module" (e.g. "Waste module" → "Waste").
 */
export function toFlagRows(apolloFlags: ReadonlyArray<FeatureFlagFragment>): FlagRow[] {
  return apolloFlags
    .filter((f) => isFeatureFlagKey(f.key))
    .map((f) => {
      const parentLabelRaw =
        f.parentKey && isFeatureFlagKey(f.parentKey)
          ? getFeatureFlagMeta(f.parentKey).label
          : f.type === "MODULE"
            ? f.label
            : null;
      const parentLabel = parentLabelRaw ? stripModuleSuffix(parentLabelRaw) : null;
      const hasAnnotation =
        (f.notes !== null && f.notes !== undefined && f.notes.trim().length > 0) ||
        f.tags.length > 0;
      return {
        // Narrow `key` from the GraphQL `String!` to the typed enum so
        // downstream consumers (drawer, dependency lookups) don't need
        // to re-validate.
        key: f.key as FeatureFlagKey,
        label: f.label,
        description: f.description,
        type: f.type as FlagRow["type"],
        parentKey: f.parentKey ?? null,
        parentLabel,
        defaultValue: f.defaultValue,
        staffOverride: f.staffOverride,
        inDevelopment: f.inDevelopment,
        requires: [...f.requires],
        notes: f.notes ?? null,
        tags: [...f.tags],
        annotationUpdatedAt: f.annotationUpdatedAt ?? null,
        annotationUpdatedBy: f.annotationUpdatedBy ?? null,
        tenantOnCount: f.tenantOnCount,
        tenantOffCount: f.tenantOffCount,
        hasAnnotation,
      };
    });
}

const PAGE_SIZE = 50; // every flag fits on one page; pagination is a no-op
const PERSISTENCE_KEY = "staff-feature-flags-by-flag";

/**
 * Flag-centric (one row per flag) view of feature flags. Pairs with
 * `OrganizationsTab` under `?tab=flags` (the default).
 *
 * Composes the lower-level Omnisearch primitives (`useOmnisearchDatagrid`
 * + `Omnisearch` + `DataGrid`) instead of `OmnisearchDataDisplay`.
 * `OmnisearchDataDisplay` would always inject `$search/$page/$pageSize/$sort`
 * into the query variables, but the `featureFlags` resolver doesn't
 * accept any of those, and declared-but-unused GraphQL variables fail
 * server-side validation. We do all filtering / sorting / paging
 * client-side, which is fine — there are <50 flags total.
 */
export function FlagsTab() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  // Track only the selected flag's key — never the row object — so that
  // when `StaffFlagsList` refetches (e.g. after saving an annotation)
  // the drawer reads the fresh row out of `flagsByKey` instead of the
  // stale snapshot captured at click time.
  const [selectedKey, setSelectedKey] = useState<FeatureFlagKey | null>(null);
  const { count: customerTenantCount } = useCustomerTenantCount();
  const { data, loading } = useQuery(STAFF_FLAGS_LIST_QUERY, {
    fetchPolicy: "cache-and-network",
  });

  const allRows = useMemo<FlagRow[]>(
    () => (data?.featureFlags ? toFlagRows(data.featureFlags) : []),
    [data]
  );

  const flagsByKey = useMemo(() => {
    const map = new Map<string, FlagRow>();
    for (const row of allRows) map.set(row.key, row);
    return map;
  }, [allRows]);

  const selectedFlag = selectedKey ? (flagsByKey.get(selectedKey) ?? null) : null;

  const columns = useMemo<OmnisearchGridColDef<FlagRow>[]>(
    // Order: Flag → Parent module → Type → Staff-visible → Default →
    // In Development → Adoption → Tags. Parent module sits second so
    // related flags read together when sorted by the (default) Flag
    // column. Parent + Adoption are tightened up since their content is
    // short; In Development gets a touch more room for its full header.
    // Tags gets the leftover space so multi-chip rows wrap cleanly.
    () => [
      {
        field: "label",
        headerName: "Flag",
        flex: 1.2,
        minWidth: 200,
        renderCell: ({ row }) => <FlagLabelCell row={row} />,
      },
      {
        field: "parentLabel",
        headerName: "Parent module",
        flex: 0.5,
        minWidth: 110,
        valueGetter: (_value, row) => row.parentLabel ?? "",
        renderCell: ({ row }) => <ParentModuleCell row={row} />,
      },
      {
        field: "type",
        headerName: "Type",
        flex: 0.5,
        minWidth: 110,
        valueGetter: (_value, row) => TYPE_LABEL[row.type],
      },
      {
        field: "staffOverride",
        headerName: "Staff-visible",
        flex: 0.4,
        minWidth: 100,
        align: "center",
        headerAlign: "center",
        renderCell: ({ row }) => <StaffOnlyCell row={row} />,
      },
      {
        field: "defaultValue",
        headerName: "Default ON/OFF",
        flex: 0.4,
        minWidth: 130,
        align: "center",
        headerAlign: "center",
        valueGetter: (_value, row) => (row.defaultValue ? "ON" : "OFF"),
        renderCell: ({ row }) => (
          <Tooltip title={`This flag defaults to ${row.defaultValue ? "ON" : "OFF"}`}>
            {/* Very pale green fill (`success.light` lightened ~70%)
                with `success.dark` text — calmer than the standard
                `success.light` shade so the chip reads as quiet
                metadata in a grid cell rather than a status alert.
                OFF keeps the neutral default chip. */}
            <Chip
              label={row.defaultValue ? "ON" : "OFF"}
              size="small"
              sx={
                row.defaultValue
                  ? (theme) => ({
                      bgcolor: lighten(theme.palette.success.light, 0.7),
                      color: theme.palette.success.dark,
                    })
                  : undefined
              }
            />
          </Tooltip>
        ),
      },
      {
        field: "inDevelopment",
        headerName: "In Development",
        flex: 0.5,
        minWidth: 130,
        align: "center",
        headerAlign: "center",
        renderCell: ({ row }) => <InDevelopmentCell row={row} />,
      },
      {
        field: "tenantOnCount",
        headerName: "Adoption",
        flex: 0.5,
        minWidth: 110,
        align: "right",
        headerAlign: "right",
        renderCell: ({ row }) => (
          <AdoptionCell
            numerator={row.tenantOnCount}
            denominator={customerTenantCount}
            offCount={row.tenantOffCount}
          />
        ),
      },
      {
        field: "tags",
        headerName: "Tags",
        flex: 1.1,
        minWidth: 180,
        sortable: false,
        valueGetter: (_value, row) => row.tags.join(", "),
        renderCell: ({ row }) => <TagsCell row={row} />,
      },
    ],
    [customerTenantCount]
  );

  // Build the dynamic Tag chip lazily off the rows we have on hand. Avoids
  // a second round-trip to `getAllFeatureFlagTags` just for chip values —
  // any tag the user could pick from autocomplete is already attached to
  // some flag in the result set.
  const additionalFilterColumns = useMemo<FilterKey[]>(() => {
    const tagValues = Array.from(new Set(allRows.flatMap((r) => r.tags))).sort();
    return [
      ...FLAG_FILTER_COLUMNS,
      {
        key: "tag",
        header: "Tag",
        filterKeyType: "enumAutocomplete",
        enumValues: tagValues,
      },
    ];
  }, [allRows]);

  const {
    omnisearch,
    debouncedOmnisearch,
    setOmnisearch,
    setOmnisearchImmediate,
    paginationModel,
    setPaginationModel,
    sortModel,
    setSortModel,
    columnFilterKeys,
  } = useOmnisearchDatagrid<FlagRow>({
    persistenceKey: PERSISTENCE_KEY,
    isURLDriven: true,
    initialSortModel: [{ field: "label", sort: "asc" }],
    initialPageSize: PAGE_SIZE,
    columns,
    additionalFilters: additionalFilterColumns,
    // Suppress the auto-derived free-text filter for any column whose
    // semantic equivalent already exists as a structured chip in
    // `additionalFilterColumns` (avoids the "Type / Type" duplicate
    // popover entries) plus any column that doesn't make sense as a
    // discrete filter (free-text label, numeric counts, dates).
    excludeFilters: [
      "label",
      "type",
      "staffOverride",
      "inDevelopment",
      "defaultValue",
      "tags",
      "tenantOnCount",
    ],
    isMobile,
  });

  const filtered = useMemo(
    () => applyFlagFilters(allRows, debouncedOmnisearch),
    [allRows, debouncedOmnisearch]
  );
  const sorted = useMemo(() => sortFlagRows(filtered, sortModel[0]), [filtered, sortModel]);
  // Even though pageSize ≥ flag count today, honoring the pagination
  // model keeps the grid behavior consistent with the rest of the app
  // and gives us headroom if we ever pass 50 flags.
  const start = paginationModel.page * paginationModel.pageSize;
  const paged = sorted.slice(start, start + paginationModel.pageSize);

  // Render the filter popover trigger as an external "Filters" button
  // (same affordance OmnisearchDataDisplay uses) so chips are obviously
  // available — the inline tune icon is too easy to miss.
  const renderFilterButton = useCallback(
    (onClick: (event: React.MouseEvent<HTMLButtonElement>) => void) => (
      <CommandButton onClick={onClick}>
        <FilterList sx={{ mr: 1 }} fontSize="small" />
        Filters
      </CommandButton>
    ),
    []
  );

  return (
    // `px: 3` matches the horizontal padding `OmnisearchDataDisplay`
    // applies via `withPadding` — keeps the grid flush with the rest of
    // the staff datagrid pages.
    <Box sx={{ px: 3 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Shows all feature flags in the system and their adoption rates.
      </Typography>
      <Stack direction="row" gap={1.5} sx={{ mb: 3 }} alignItems="center" flexWrap="wrap">
        <Omnisearch
          omnisearch={omnisearch}
          setOmnisearch={setOmnisearch}
          setOmnisearchImmediate={setOmnisearchImmediate}
          onApplyFilters={setOmnisearchImmediate}
          columnFilterKeys={columnFilterKeys}
          persistenceKey={PERSISTENCE_KEY}
          showFilterButtonInside={false}
          showExternalFilterButton
          renderFilterButton={renderFilterButton}
          sx={{ flexGrow: 1 }}
        />
      </Stack>
      <DataGrid<FlagRow>
        rows={paged}
        columns={columns}
        getRowId={(row) => row.key}
        autoHeight
        getRowHeight={() => "auto"}
        loading={loading}
        pagination
        paginationMode="client"
        sortingMode="client"
        filterMode="client"
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        sortModel={sortModel}
        onSortModelChange={setSortModel}
        pageSizeOptions={[PAGE_SIZE]}
        onRowClick={(params) => setSelectedKey(params.row.key)}
        sx={{ "& .MuiDataGrid-row": { cursor: "pointer" } }}
        hideFooterPagination={sorted.length <= PAGE_SIZE}
        rowCount={sorted.length}
      />

      <FlagDetailDrawer
        flag={selectedFlag}
        flagsByKey={flagsByKey}
        customerTenantCount={customerTenantCount}
        onSelectFlag={(f) => setSelectedKey(f.key)}
        onClose={() => setSelectedKey(null)}
      />
    </Box>
  );
}
